import { useState } from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import { useTranslation } from '@crehana/i18n';
import { date } from '@crehana/utils';
import { useRouter } from 'next/router';

import InfoDialog from '@/views/AdminDashboard/components/InfoDialog';
import {
  type InfoDialogProps,
  InfoDialogTypeEnum,
} from '@/views/AdminDashboard/components/InfoDialog/types';
import useAdminDashboard from '@/views/AdminDashboard/hooks/useAdminDashboard';
import { DIALOG_SUCCESS_ICON } from '@/views/AdminDashboard/pages/Reports/constants';

import type { UseFormSubmitInfoDialogProps } from './types';

const useFormSubmitInfoDialog = ({
  redirect,
  amplitude,
  descriptionRight,
  title,
  description,
  buttonLabel,
  handleSurvey,
}: UseFormSubmitInfoDialogProps) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { logEvent } = useAmplitude();
  const { currentUserOrganization, organizationId, isCorporation, slug } =
    useAdminDashboard();
  const [infoDialogProps, setInfoDialogProps] = useState({} as InfoDialogProps);

  const closeState = (prevState: InfoDialogProps) => ({
    ...prevState,
    open: false,
  });

  const openState = (isFormCancelDialog?: boolean) => {
    const type = isFormCancelDialog
      ? InfoDialogTypeEnum.Back
      : InfoDialogTypeEnum.Success;

    const props: InfoDialogProps = {
      type,
      open: true,
      title: title && t(`${title}${type}`),
      description: description && t(`${description}${type}`),
      descriptionRight,
      buttonLabel: buttonLabel && t(`${buttonLabel}${type}`),
      headerImage:
        (type === InfoDialogTypeEnum.Success && DIALOG_SUCCESS_ICON) ||
        undefined,
      onDismiss: handleDismissDialog,
    };

    switch (type) {
      case InfoDialogTypeEnum.Back:
        props.onConfirm = handleMainAction;
        break;
      case InfoDialogTypeEnum.Success:
        props.onDismiss = handleMainAction;
        props.onConfirm = undefined;
        break;
      default:
        props.onConfirm = undefined;
        break;
    }

    return props;
  };

  const handleOpenDialog = (isFormCancelDialog?: boolean) => {
    setInfoDialogProps(openState(isFormCancelDialog));
  };

  const handleDismissDialog = () => {
    setInfoDialogProps(closeState);
  };

  const handleMainAction = (dialogType: InfoDialogTypeEnum) => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (handleSurvey) handleSurvey();
    logAmplitude(dialogType);
    router.push(redirect);

    setInfoDialogProps(closeState);
  };

  const logAmplitude = (dialogType: InfoDialogTypeEnum) => {
    const amplitudeData = {
      ...getSourceEventParams(),
      'Month Date': date.getMonthName(),
      'Day of the week': date.format(new Date(), 'dddd'),
      'Organization Session': router.asPath.split('/')[2],
      user: {
        'Current User ORG': currentUserOrganization,
        'Organization Type': isCorporation,
        'Organization Id': organizationId,
        'Organization Slug': slug,
      },
      ...amplitude?.extraData,
    };
    logEvent({
      event:
        dialogType === InfoDialogTypeEnum.Success
          ? amplitude.onSend
          : amplitude.onCancel,
      data: amplitudeData,
    });
  };

  return {
    openDialog: handleOpenDialog,
    infoDialog: <InfoDialog {...infoDialogProps} />,
  };
};

export default useFormSubmitInfoDialog;
